import { useMemo, useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import {
  Activity,
  ArrowRight,
  Calendar,
  Database,
  Inbox,
  MoreHorizontal,
  Sparkles,
} from 'lucide-react'
import { useOrgQuery } from '@/lib/hooks/useOrgQuery'
import { formatDate } from '@/lib/utils'
import { MetricCard, type Metric } from '@/components/admin/MetricCard'
import { UserRow, type AdminUser } from '@/components/admin/UserRow'
import { PageHeader } from '@/components/page-header'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  EcosystemMark,
  Eyebrow,
} from '@/components/hawk-primitives'

interface ProxyStats {
  users: number
  repositories: number
  packages: number
  admitted_last_hour: number
}

interface RepoSummary {
  name: string
  ecosystem: string
  package_count: number
  last_fetched_at: string | null
}

export const Route = createFileRoute('/_app/org/')({
  component: AdminPage,
})

function AdminPage() {
  const stats = useOrgQuery<ProxyStats>(['admin-stats'], '/admin/stats', {
    refetchInterval: 30_000,
    staleTime: 25_000,
  })

  const repos = useOrgQuery<RepoSummary[]>(['admin-repositories'], '/admin/repositories', {
    refetchInterval: 30_000,
    staleTime: 25_000,
  })

  const users = useOrgQuery<AdminUser[]>(['admin-users'], '/admin/users')

  return (
    <div className="px-10 py-8">
      <PageHeader
        title="Operator console"
        description="Live snapshot of the registry: recent admissions, repository health, and user access."
        right={
          <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5">
            <Calendar className="size-3" />
            Last 24 hours
          </Button>
        }
      />

      <ActivitySection stats={stats.data} repos={repos.data ?? []} />
      <PanelRow repos={repos.data ?? []} loading={repos.isLoading} />
      <RequestsPanel />
      <UsersBlock
        users={users.data ?? []}
        loading={users.isLoading}
      />
    </div>
  )
}

function ActivitySection({
  stats,
  repos,
}: {
  stats?: ProxyStats
  repos: RepoSummary[]
}) {
  const repoMetrics: Metric[] = useMemo(() => {
    const activeCount = repos.filter((r) => r.last_fetched_at).length
    return [
      {
        label: 'Configured',
        value: stats?.repositories?.toString() ?? '—',
        spark: synthSpark(12, stats?.repositories ?? 0, 0.2),
      },
      {
        label: 'Active recently',
        value: activeCount.toString(),
        delta:
          activeCount > 0
            ? { value: `+${activeCount}`, direction: 'up', positive: true }
            : undefined,
        spark: synthSpark(12, activeCount, 0.4),
      },
    ]
  }, [repos, stats?.repositories])

  const admissionsMetrics: Metric[] = useMemo(() => {
    const last = stats?.admitted_last_hour ?? 0
    const recent = repos
      .filter((r) => r.last_fetched_at)
      .map((r) => new Date(r.last_fetched_at!).getTime())
    const now = Date.now()
    const lastDay = recent.filter((t) => now - t < 86_400_000).length
    return [
      {
        label: 'Last hour',
        value: last.toString(),
        delta:
          last > 0
            ? { value: `+${last}`, direction: 'up', positive: true }
            : undefined,
        spark: synthSpark(12, Math.max(1, last), 0.7),
      },
      {
        label: 'Last 24h',
        value: lastDay.toString(),
        spark: synthSpark(12, Math.max(1, lastDay), 0.5),
      },
    ]
  }, [stats, repos])

  return (
    <section className="mb-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <MetricCard title="Repositories" icon={Database} metrics={repoMetrics} />
        <MetricCard
          title="Admissions"
          icon={Activity}
          metrics={admissionsMetrics}
        />
      </div>
    </section>
  )
}

function PanelRow({
  repos,
  loading,
}: {
  repos: RepoSummary[]
  loading: boolean
}) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-10">
      <RepositoriesPanel repos={repos} loading={loading} />
      <AdmissionsPanel repos={repos} loading={loading} />
    </div>
  )
}

interface RequestSummary {
  id: string
  repository_name: string
  package_name: string
  package_version: string | null
  ecosystem: string
  status: string
  requested_at: string
  requester_name: string | null
  requester_email: string | null
}

function RequestsPanel() {
  const requests = useOrgQuery<RequestSummary[]>(
    ['admin-requests-overview'],
    '/admin/requests?limit=200',
    { refetchInterval: 20_000, staleTime: 15_000 },
  )

  const all = Array.isArray(requests.data) ? requests.data : []
  const openItems = useMemo(
    () =>
      all
        .filter((r) =>
          ['pending', 'verified', 'unverified'].includes(r.status),
        )
        .slice(0, 6),
    [all],
  )

  const openCount = all.filter((r) =>
    ['pending', 'verified', 'unverified'].includes(r.status),
  ).length

  return (
    <section className="mb-10">
      <div className="border border-border bg-card rounded-md overflow-hidden">
        <div className="flex items-center gap-2 px-5 py-3 border-b border-border bg-background/30">
          <Inbox
            className="size-3.5 text-muted-foreground"
            strokeWidth={1.5}
          />
          <Eyebrow>Open package requests</Eyebrow>
          <span className="font-mono text-[10px] text-foreground/80 tabular-nums tracking-wider">
            {openCount.toString().padStart(2, '0')}
          </span>
          <div className="ml-auto">
            <Button asChild variant="ghost" size="xs" className="font-mono uppercase tracking-wider">
              <Link to="/org/requests">
                Review all
                <ArrowRight className="size-3" />
              </Link>
            </Button>
          </div>
        </div>

        {requests.isLoading ? (
          <PanelEmpty>Loading...</PanelEmpty>
        ) : openItems.length === 0 ? (
          <PanelEmpty>No open requests.</PanelEmpty>
        ) : (
          <ul className="divide-y divide-border">
            {openItems.map((r) => (
              <li key={r.id}>
                <Link
                  to="/org/requests"
                  className="group flex items-center gap-3 px-5 py-3 hover:bg-popover transition-colors"
                >
                  <EcosystemMark ecosystem={r.ecosystem} size="sm" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-sm font-medium truncate">
                        {r.package_name}
                      </span>
                      {r.package_version && (
                        <span className="font-mono text-[11px] px-1.5 py-0.5 rounded bg-primary/10 text-primary ring-1 ring-inset ring-primary/30 tabular-nums shrink-0">
                          {r.package_version}
                        </span>
                      )}
                    </div>
                    <div className="font-mono text-[10px] text-muted-foreground mt-0.5 flex items-center gap-x-4 gap-y-1 flex-wrap">
                      <span>
                        in{' '}
                        <span className="text-foreground/70">{r.repository_name}</span>
                      </span>
                      <span>{r.requester_name ?? r.requester_email ?? 'unknown'}</span>
                      <span>{formatDate(r.requested_at)}</span>
                    </div>
                  </div>
                  <RequestStatusPill status={r.status} />
                  <ArrowRight
                    className="size-3.5 text-muted-foreground/30 group-hover:text-muted-foreground group-hover:translate-x-0.5 transition-all"
                    strokeWidth={1.5}
                  />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  )
}

function RequestStatusPill({ status }: { status: string }) {
  const config: Record<string, { label: string; cls: string; icon?: React.ReactNode }> = {
    pending: {
      label: 'Pending',
      cls: 'bg-secondary text-muted-foreground ring-border',
    },
    verified: {
      label: 'Verified',
      cls: 'bg-primary/10 text-primary ring-primary/30',
      icon: <Sparkles className="size-2.5" strokeWidth={1.5} />,
    },
    unverified: {
      label: 'Unverified',
      cls: 'bg-secondary text-muted-foreground ring-border',
    },
  }
  const c = config[status] ?? config.pending
  return (
    <span
      className={`inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.16em] px-1.5 py-0.5 rounded ring-1 ring-inset shrink-0 ${c?.cls ?? config.pending!.cls}`}
    >
      {c?.icon}
      {c?.label ?? status}
    </span>
  )
}

function RepositoriesPanel({
  repos,
  loading,
}: {
  repos: RepoSummary[]
  loading: boolean
}) {
  return (
    <Panel
      title="Repositories"
      count={repos.length}
      action={{ label: 'Manage', to: '/org/repositories' }}
    >
      {loading ? (
        <PanelEmpty>Loading...</PanelEmpty>
      ) : repos.length === 0 ? (
        <PanelEmpty>None configured yet.</PanelEmpty>
      ) : (
        <ul className="divide-y divide-border">
          {repos.map((repo) => (
            <li
              key={repo.name}
              className="group px-5 py-3 flex items-center gap-3 hover:bg-popover transition-colors relative"
            >
              <span
                className={`absolute left-0 top-2 bottom-2 w-[2px] transition-opacity ${
                  repo.last_fetched_at
                    ? 'bg-primary opacity-80'
                    : 'bg-muted-foreground/30 opacity-0 group-hover:opacity-100'
                }`}
              />
              <EcosystemMark ecosystem={repo.ecosystem} size="sm" />
              <span className="flex-1 min-w-0 truncate text-sm font-medium">
                {repo.name}
              </span>
              <span className="font-mono text-[10px] text-muted-foreground tabular-nums shrink-0 uppercase tracking-wider">
                {repo.package_count.toLocaleString()} pkg
              </span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  )
}

function AdmissionsPanel({
  repos,
  loading,
}: {
  repos: RepoSummary[]
  loading: boolean
}) {
  const events = useMemo(() => {
    return repos
      .filter((r) => r.last_fetched_at)
      .sort(
        (a, b) =>
          new Date(b.last_fetched_at!).getTime() -
          new Date(a.last_fetched_at!).getTime(),
      )
      .slice(0, 8)
  }, [repos])

  return (
    <Panel title="Recent admissions" count={events.length}>
      {loading ? (
        <PanelEmpty>Loading...</PanelEmpty>
      ) : events.length === 0 ? (
        <PanelEmpty>Nothing fetched yet.</PanelEmpty>
      ) : (
        <ul className="divide-y divide-border">
          {events.map((repo) => (
            <li
              key={repo.name}
              className="px-5 py-3 flex items-center gap-3 hover:bg-popover transition-colors"
            >
              <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground tabular-nums shrink-0 w-12">
                {formatDate(repo.last_fetched_at!)}
              </span>
              <EcosystemMark ecosystem={repo.ecosystem} size="sm" />
              <span className="flex-1 min-w-0 truncate text-sm font-mono text-foreground/90">
                {repo.name}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  )
}


function Panel({
  title,
  count,
  action,
  children,
}: {
  title: string
  count?: number
  action?: { label: string; to: string }
  children: React.ReactNode
}) {
  const [menuOpen, setMenuOpen] = useState(false)
  return (
    <div className="relative border border-border bg-card rounded-md overflow-hidden flex flex-col min-h-[260px]">
      <div className="flex items-center gap-2 px-5 py-3 border-b border-border bg-background/30">
        <Eyebrow>{title}</Eyebrow>
        {count !== undefined && (
          <span className="font-mono text-[10px] text-foreground/80 tabular-nums tracking-wider">
            {count.toString().padStart(2, '0')}
          </span>
        )}
        <div className="ml-auto flex items-center gap-1">
          {action && (
            <Button asChild variant="ghost" size="xs" className="font-mono uppercase tracking-wider">
              <Link to={action.to}>
                {action.label}
                <ArrowRight className="size-3" />
              </Link>
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon-xs"
            onClick={() => setMenuOpen(!menuOpen)}
            className="text-muted-foreground"
            aria-label={menuOpen ? 'Hide tile options' : 'Show tile options'}
            aria-expanded={menuOpen}
          >
            <MoreHorizontal className="size-3.5" aria-hidden />
          </Button>
        </div>
      </div>
      <div className="flex-1 min-h-0 overflow-auto">{children}</div>
    </div>
  )
}

function PanelEmpty({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-center h-full py-10 text-sm text-muted-foreground font-mono uppercase tracking-wider text-[11px]">
      {children}
    </div>
  )
}

function UsersBlock({
  users,
  loading,
}: {
  users: AdminUser[]
  loading: boolean
}) {
  return (
    <section>
      <div className="flex items-baseline justify-between mb-4">
        <div className="flex items-baseline gap-3">
          <Eyebrow>Users</Eyebrow>
          <span className="font-mono text-xs text-muted-foreground tabular-nums">
            {users.length} registered
          </span>
        </div>
      </div>
      <div className="border border-border bg-card rounded-md overflow-hidden">
        {loading ? (
          <div className="py-10 text-center font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
            Loading...
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-border bg-background/30">
                <TableHead className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  Name
                </TableHead>
                <TableHead className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  Email
                </TableHead>
                <TableHead className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  Joined
                </TableHead>
                <TableHead className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  Last login
                </TableHead>
                <TableHead className="w-[140px] text-right font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  Status
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.length > 0 ? (
                users.map((user) => (
                  <UserRow key={user.id} user={user} />
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="text-center text-sm text-muted-foreground py-10"
                  >
                    No users yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </div>
    </section>
  )
}

function synthSpark(n: number, peak: number, intensity: number): number[] {
  const out: number[] = []
  for (let i = 0; i < n; i++) {
    const t = i / (n - 1)
    const wave = Math.sin(t * 4.2) * 0.5 + 0.5
    const noise = Math.sin(i * 13.7) * 0.3 + 0.3
    out.push(Math.max(0.2, peak * intensity * (wave * 0.7 + noise * 0.3)))
  }
  return out
}
